/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { UserEdition } from "@encoo-web/encoo-lib";
import { Button, Form, Modal, Spin, Table, Tabs } from "antd";
import moment from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import CompanyDetail from "src/containers/userManage/components/CompanyDetail";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { CompanyList, UserDetailData } from "src/models/userManage";
import { UserSeviceClient } from "src/services/UserManagementService";

const formItemCss = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  line-height: 14px;
  color: rgb(61, 73, 102);
  font-weight: 600;
`;
const divColor = css`
  margin-top: 12px;
  color: rgb(143, 155, 179);
  background-color: rgb(247, 249, 252);
  padding: 10px 13px;
  min-height: 32px;
`;
export default React.memo(
  (props: { isView: boolean; userId: string; onClose: () => void }) => {
    const { isView, userId, onClose } = props;
    const formatMessage = useFormatMessage();
    const [form] = Form.useForm();
    const [isCompanyDetail, setIsCompanyDetail] = useState(false);
    const [companyDetail, setCompanyDetail] = useState({} as CompanyList);
    const [detail, setDetail] = useState({} as UserDetailData);
    const [loading, setLoading] = useState(false);
    const [activeKey, setActiveKey] = useState("1");

    useEffect(() => {
      setLoading(true);
      let isUnmounted = false;
      UserSeviceClient.getUserDetail(userId).then((res) => {
        if (!isUnmounted) {
          setDetail(res.data);
          setLoading(false);
        }
      });
      return () => {
        isUnmounted = true;
      };
    }, [userId]);

    const onView = useCallback((record: CompanyList) => {
      setIsCompanyDetail(true);
      setCompanyDetail(record);
    }, []);

    const onCompanyClose = useCallback(() => {
      setIsCompanyDetail(false);
    }, []);

    const columns = [
      {
        title: formatMessage(messageIds.company.companyName),
        dataIndex: "name",
      },
      {
        title: formatMessage(messageIds.company.companyType),
        dataIndex: "edition",
        render: (text: UserEdition) => (
          <span>
            {Number(UserEdition[text]) === 0
              ? formatMessage(messageIds.edition.enterprise)
              : Number(UserEdition[text]) === 1
              ? formatMessage(messageIds.edition.community)
              : undefined}
          </span>
        ),
      },
      {
        title: formatMessage(messageIds.common.createdBy),
        dataIndex: "createdByName",
      },
      {
        title: formatMessage(messageIds.common.createTime),
        dataIndex: "createdAt",
        render: (text: string) => (
          <span>{moment(text).format("YYYY-MM-DD HH:mm:ss")}</span>
        ),
      },
      {
        title: formatMessage(messageIds.common.operation),
        dataIndex: "operation",
        render: (text: string, record: CompanyList) => (
          <Button
            type="link"
            css={css`
              padding-left: 0;
            `}
            onClick={() => onView(record)}
          >
            {formatMessage(messageIds.common.detail)}
          </Button>
        ),
      },
    ];

    const formContext = useMemo(() => {
      return (
        <Fragment>
          <Form form={form} layout="vertical">
            <Form.Item
              label={formatMessage(messageIds.userManagement.columns.userName)}
              name="userName"
              css={formItemCss}
            >
              <div css={divColor}>{detail?.userName}</div>
            </Form.Item>
            <Form.Item
              label={formatMessage(messageIds.common.createTime)}
              name="createAt"
              css={formItemCss}
            >
              <div css={divColor}>
                {moment(detail?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </div>
            </Form.Item>
            <Form.Item
              label={formatMessage(
                messageIds.userManagement.columns.phoneNumber
              )}
              name="phoneNumber"
              css={formItemCss}
            >
              <div css={divColor}>{detail?.phoneNumber}</div>
            </Form.Item>
            <Form.Item
              label={formatMessage(messageIds.userManagement.columns.email)}
              name="email"
              css={formItemCss}
            >
              <div css={divColor}>{detail?.email}</div>
            </Form.Item>
            <Form.Item
              label={formatMessage(
                messageIds.userManagement.columns.lastLoginedAt
              )}
              name="lastLoginedAt"
              css={formItemCss}
            >
              <div css={divColor}>
                {detail?.lastLoginedAt &&
                  moment(detail?.lastLoginedAt).format("YYYY-MM-DD HH:ss:mm")}
              </div>
            </Form.Item>
            <Form.Item
              label={formatMessage(
                messageIds.userManagement.columns.lastModifiedAt
              )}
              name="lastModifiedAt"
              css={formItemCss}
            >
              <div css={divColor}>
                {detail?.lastModifiedAt &&
                  moment(detail?.lastModifiedAt).format("YYYY-MM-DD HH:mm:ss")}
              </div>
            </Form.Item>
          </Form>
        </Fragment>
      );
    }, [detail, form, formatMessage]);

    const onModalClose = useCallback(() => {
      setActiveKey("1");
      onClose();
    }, [onClose]);

    return (
      <div>
        <Modal
          visible={isView}
          width="40%"
          onCancel={onModalClose}
          footer={""}
          css={css`
            .ant-modal-header {
              border-bottom: none;
            }
          `}
        >
          <Tabs
            type="card"
            activeKey={activeKey}
            tabBarStyle={{
              height: "30px",
              lineHeight: "30px",
            }}
            tabBarGutter={0}
            onChange={(activekey) => {
              setActiveKey(activekey);
            }}
            css={css`
              .ant-tabs-tab {
                background-color: #ffffff !important;
                border-radius: 4px 0px 0px 0px;
                width: 130px;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid #ffffff;
              }
              .ant-tabs-tab.ant-tabs-tab-active {
                background-color: #84adff !important;
              }
              .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                color: white;
              }
            `}
          >
            <Tabs.TabPane
              tab={formatMessage(messageIds.userManagement.userDetail)}
              key="1"
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.userManagement.userDetail)}
                </h1>
              </div>
              <div
                css={css`
                  margin-top: 20px;
                `}
              >
                {loading ? (
                  <Spin
                    tip="Loading..."
                    css={css`
                      position: initial !important;
                    `}
                  >
                    {formContext}
                  </Spin>
                ) : (
                  formContext
                )}
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={formatMessage(messageIds.userManagement.associatedCompany)}
              key="2"
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.userManagement.associatedCompany)}
                </h1>
              </div>
              <div
                css={css`
                  margin-top: 20px;
                `}
              >
                <Table
                  columns={columns}
                  dataSource={detail?.companies}
                  rowKey={(record) => record?.id}
                ></Table>
              </div>
            </Tabs.TabPane>
          </Tabs>

          <div
            css={css`
              text-align: right;
              margin-top: 20px;
            `}
          >
            <Button
              type="primary"
              css={css`
                height: 32px;
                min-width: 88px;
                border-radius: 4px;
              `}
              onClick={onModalClose}
            >
              {formatMessage(messageIds.common.confirm)}
            </Button>
          </div>
        </Modal>
        <CompanyDetail
          isCompanyDetail={isCompanyDetail}
          companyDetail={companyDetail}
          onClose={onCompanyClose}
        ></CompanyDetail>
      </div>
    );
  }
);
